// Library Methods
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import PatientInfoSection from "./PatientInfoSection/PatientInfoSection";
import SharedTabs from "src/components/shared/navigation/SharedTabs/SharedTabs";
import ErrorText from "src/components/shared/text/ErrorText/ErrorText";
import ExamInsights from "./ExamInsights/ExamInsights";
import ProgressionAnalyses from "./ProgressionAnalyses/ProgressionAnalyses";
import Exams from "./Exams/Exams";
import { EyeInsightIcon } from "src/assets/icons";

// Constants
import { EXAMS_TAB_ID, PROGRESSION_ANALYSES_TAB_ID } from "src/constants/enums";

// Types
import { PatientType } from "src/services/patients/types";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { ExamSliderProvider } from "src/contexts/exams/ExamSliderContext";
import { useExternalExamsContext } from "src/contexts/exams/ExternalExamsContext";

// Hooks
import { useProgressionAnalyses } from "src/services/exams/hooks/progression-analyses/useProgressionAnalyses";

// Styles
import { ModalCustomConfirm } from "src/styles/mui/muiStylesHelper";

type ClinicExamsType = {
  selectedPatient: PatientType | undefined;
};

const ClinicExams = ({ selectedPatient }: ClinicExamsType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();
  const location = useLocation();

  const {
    hasExternalExams,
    actions: { setOpenCompareModal },
  } = useExternalExamsContext();

  const {
    data: analysisData,
    isLoading: isLoadingAnalyses,
    isFetching: isFetchingAnalyses,
    isRefetching: isRefetchingAnalyses,
  } = useProgressionAnalyses(
    clinicInfo?.id,
    clinicInfo?.timezone,
    selectedPatient?.id
  );

  const isLoadingProgressionAnalysis = useMemo(
    () => isLoadingAnalyses || isFetchingAnalyses || isRefetchingAnalyses,
    [isLoadingAnalyses, isFetchingAnalyses, isRefetchingAnalyses]
  );

  const formattedPatientName = useMemo(
    () => `${selectedPatient?.firstName} ${selectedPatient?.lastName}`,
    [selectedPatient]
  );

  const examTabs = useMemo(
    () => [
      { id: EXAMS_TAB_ID, label: t("Exams"), showTab: true },
      {
        id: PROGRESSION_ANALYSES_TAB_ID,
        label: t("Progression Analyses"),
        showTab:
          Boolean(clinicInfo?.licensing?.canUseProgressionReports) ||
          Boolean(
            analysisData &&
              analysisData?.length > 0 &&
              !isLoadingProgressionAnalysis
          ),
      },
    ],
    [clinicInfo?.licensing, analysisData, isLoadingProgressionAnalysis, t]
  );

  const [selectedTab, setSelectedTab] = useState(EXAMS_TAB_ID);

  // Start each patient on the Exams tab, unless a report linked back to this
  // patient asking for a specific tab
  useEffect(() => {
    const isLinkedPatient =
      Boolean(selectedPatient?.id) &&
      location.state?.stateId === selectedPatient?.id;

    setSelectedTab(
      isLinkedPatient
        ? location.state?.selectedTab ?? EXAMS_TAB_ID
        : EXAMS_TAB_ID
    );
    // only when the patient changes, not on every navigation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient?.id]);

  return (
    <Box display="flex" width="100%" minWidth={0} flexDirection="column">
      <Typography
        height="40px"
        alignContent="center"
        fontWeight={600}
        textOverflow="ellipsis"
        overflow="hidden"
        whiteSpace="nowrap"
        maxWidth="100%"
        minWidth={0}
      >
        {selectedPatient
          ? `${t("Exams")}: ${formattedPatientName}`
          : t("Exams")}
      </Typography>

      {!selectedPatient && (
        <ErrorText
          header={t("No Patient Currently Selected.")}
          message={t(
            "Exams will be displayed once a patient is selected from the patients list."
          )}
        />
      )}

      {selectedPatient && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <PatientInfoSection patient={selectedPatient} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              columnGap: "8px",
              width: "100%",
              borderBottom: "1px solid #E2772E",
            }}
          >
            <SharedTabs
              tabs={examTabs}
              selectedTabId={selectedTab}
              onChange={(tabId) => setSelectedTab(tabId)}
              showBorder={false}
            />
            {Boolean(clinicInfo?.licensing?.canUseExternalReports) &&
              hasExternalExams && (
                <Button
                  sx={{ ...ModalCustomConfirm(), marginBottom: "8px" }}
                  startIcon={<EyeInsightIcon />}
                  onClick={() => setOpenCompareModal(true)}
                >
                  {t("Exam Insights")}
                </Button>
              )}
          </Box>

          {selectedTab === EXAMS_TAB_ID && (
            <ExamSliderProvider>
              <Exams selectedPatient={selectedPatient} />
            </ExamSliderProvider>
          )}
          {selectedTab === PROGRESSION_ANALYSES_TAB_ID && (
            <ProgressionAnalyses selectedPatient={selectedPatient} />
          )}

          <ExamInsights />
        </Box>
      )}
    </Box>
  );
};

export default ClinicExams;
